import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { getFilenameFromContentDisposition } from "../../helpers";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { ERROR_MESSAGES } from "../../constants/enums";
import { IAutomobilePolicyState } from "./automobilePolicyChangeReq.types";
import { makeApiCall } from "../../helpers/postRequest";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { uploadTaskFile } from "../tasks/tasksActions";
import { IInternalLogEntry } from "../accessLogs/accessLogs.types";
import {
  EDIT_POLICY_CHANGE_REQUEST_V2,
  EDIT_PUBLIC_POLICY_CHANGE_REQUEST_V2,
  GET_POLICY_CHANGE_REQUEST_V2,
  POLICY_CHANGE_V2_TYPE,
  POLICY_CHANGE_V2_GET_TYPE,
  buildPolicyChangeV2Query,
  omitPolicyChangeV2Fields,
} from "../../constants/policyChangeV2.constants";

export const FETCH_AUTOMOBILE_POLICY_REQ_LIST_PROGRESS =
  "FETCH_AUTOMOBILE_POLICY_REQ_LIST_PROGRESS";
export const FETCH_AUTOMOBILE_POLICY_REQ_LIST_SUCCESS =
  "FETCH_AUTOMOBILE_POLICY_REQ_LIST_SUCCESS";
export const FETCH_AUTOMOBILE_POLICY_REQ_LIST_FAILED =
  "FETCH_AUTOMOBILE_POLICY_REQ_LIST_FAILED";

export const fetchAutomobilePolicyReqListProgress = () =>
  action(FETCH_AUTOMOBILE_POLICY_REQ_LIST_PROGRESS);
export const fetchAutomobilePolicyReqListSuccess = (
  list: IAutomobilePolicyState[]
) =>
  action(FETCH_AUTOMOBILE_POLICY_REQ_LIST_SUCCESS, {
    list,
  });
export const fetchAutomobilePolicyReqListFailed = () =>
  action(FETCH_AUTOMOBILE_POLICY_REQ_LIST_FAILED);

export const fetchAutomobilePolicyReqListAsync =
  (
    customerpolicyId: number,
    status: string,
    searchValue: IDatatableV2AdvancedSearchFilter,
    date: IDataTableV2DateState["dates"],
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchAutomobilePolicyReqListProgress());
      let finalUrl =
        GET_POLICY_CHANGE_REQUEST_V2 +
        buildPolicyChangeV2Query({
          policy_type: POLICY_CHANGE_V2_GET_TYPE.AUTO,
          customer_policy_id: customerpolicyId,
          from_date: date.fromDate,
          to_date: date.toDate,
        });

      if (status !== undefined && status !== "-1") {
        finalUrl += "&status=" + status;
      }
      if (searchValue.length > 0) {
        finalUrl += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      const res = await api.get(finalUrl);
      const data: IAutomobilePolicyState[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchAutomobilePolicyReqListSuccess(data));
    } catch (err: any) {
      dispatch(fetchAutomobilePolicyReqListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_PROGRESS =
  "FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_PROGRESS";
export const FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_SUCCESSS =
  "FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_SUCCESSS";
export const FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_FAILED =
  "FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_FAILED";

export const fetchAutomobilePolicyChangeReqProgress = () =>
  action(FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_PROGRESS);
export const fetchAutomobilePolicyChangeReqSuccess = (
  data: IAutomobilePolicyState
) => action(FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_SUCCESSS, { data });
export const fetchAutomobilePolicyChangeReqFailed = (error?: string) =>
  action(FETCH_AUTOMOBILE_POLICY_CHANGE_REQ_APPLICATION_FAILED, { error });

export const fetchAutomobilePolicyChangeReqAsync =
  (
    customerPolicyId: number,
    incrementalNumber: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchAutomobilePolicyChangeReqProgress());
      const res = await api.get(
        `/policy/get-policy-auto-policy-change-request?customer_policy_id=${customerPolicyId}&change_request_code=${incrementalNumber}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchAutomobilePolicyChangeReqSuccess({ ...data[0] }));
      } else {
        dispatch(
          fetchAutomobilePolicyChangeReqFailed(ERROR_MESSAGES.NO_RECORD_FOUND)
        );
      }
    } catch (err: any) {
      dispatch(fetchAutomobilePolicyChangeReqFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addAutomobilePolicyChange =
  (
    data: IAutomobilePolicyState,
    initialData: IAutomobilePolicyState,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean, data?: IAutomobilePolicyState) => void,
    logData?: IInternalLogEntry,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: data.customer_id,
        policy_type: data.policy_type,
        customer_policy_id: data.customer_policy_id,
        page_name: "Automobile Policy Change",
      };

      const updateFilesJson = await uploadTaskFile(
        data.attachments as any,
        "POLICY",
        asPayload
      );
      const res = await makeApiCall(
        {
          url: "/policy/edit-policy-auto-policy-change-request",
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "change_request_code",
          },
        },
        initialData,
        {
          ...data,
          attachments: updateFilesJson,
        }
      );

      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          message: "Policy Changed successfully!",
          displayAs: "snackbar",
        })
      );
      dispatch(fetchAutomobilePolicyChangeReqSuccess(data));
      if (logData) {
        dispatch({ type: "custom/log", logData });
      }
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const downloadAutomobilePolicyChangeReqPDF =
  (
    customerPolicyId: number,
    change_request_code: string,
    policyType: string,
    getBufferOnly?: (buffer: string) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-policy-change-pdf?customer_policy_id=${customerPolicyId}&change_request_code=${change_request_code}&policy_type=${policyType}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      if(getBufferOnly){
        getBufferOnly(pdfBuffer)
        return;
      }

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = getFilenameFromContentDisposition(res);
      link.click();

      URL.revokeObjectURL(pdfUrl);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const previewAutomobilePolicyChangeReqPDF =
  (
    customerPolicyId: number,
    change_request_code: string,
    policyType: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-policy-change-pdf?customer_policy_id=${customerPolicyId}&change_request_code=${change_request_code}&policy_type=${policyType}&is_preview=true`
      );

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
    } catch (err: any) {
      // dispatch(fetchCommAutomobileLiabilitySlipsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_AUTOMOBILE_POLICY_REQ_APPLICATION_STATE =
  "CLEAR_AUTOMOBILE_POLICY_REQ_APPLICATION_STATE";

export const clearAutomobilePolicyReqState = () =>
  action(CLEAR_AUTOMOBILE_POLICY_REQ_APPLICATION_STATE);

// ==========================================================================
// v2: consolidated policy-change-request endpoints
// POST /policy/edit-policy-change-request  (discriminates by policy_type)
// GET  /policy/get-policy-change-request   (filtered list)
// ==========================================================================

export const fetchAutomobilePolicyChangeReqV2Async =
  (
    customerPolicyId: number,
    incrementalNumber: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchAutomobilePolicyChangeReqProgress());
      const finalUrl =
        GET_POLICY_CHANGE_REQUEST_V2 +
        buildPolicyChangeV2Query({
          policy_type: POLICY_CHANGE_V2_GET_TYPE.AUTO,
          customer_policy_id: customerPolicyId,
          change_request_code: incrementalNumber,
          limit: 1,
        });
      const res = await api.get(finalUrl);
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchAutomobilePolicyChangeReqSuccess({ ...data[0] }));
      } else {
        dispatch(
          fetchAutomobilePolicyChangeReqFailed(ERROR_MESSAGES.NO_RECORD_FOUND)
        );
      }
    } catch (err: any) {
      dispatch(fetchAutomobilePolicyChangeReqFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addAutomobilePolicyChangeV2 =
  (
    data: IAutomobilePolicyState,
    initialData: IAutomobilePolicyState,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean, data?: IAutomobilePolicyState) => void,
    logData?: IInternalLogEntry,
    isPublic?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: data.customer_id,
        policy_type: data.policy_type,
        customer_policy_id: data.customer_policy_id,
        page_name: "Automobile Policy Change",
      };

      const updateFilesJson = await uploadTaskFile(
        data.attachments as any,
        "POLICY",
        asPayload
      );
      const res = await makeApiCall(
        {
          url: isPublic
            ? EDIT_PUBLIC_POLICY_CHANGE_REQUEST_V2
            : EDIT_POLICY_CHANGE_REQUEST_V2,
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "change_request_code",
          },
        },
        omitPolicyChangeV2Fields(initialData),
        {
          ...omitPolicyChangeV2Fields(data),
          policy_type: POLICY_CHANGE_V2_TYPE.AUTO,
          attachments: updateFilesJson,
        },
        isPublic
      );

      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          message: "Policy Changed successfully!",
          displayAs: "snackbar",
        })
      );
      dispatch(fetchAutomobilePolicyChangeReqSuccess(data));
      if (logData) {
        dispatch({ type: "custom/log", logData });
      }
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
